import { isWorkshopParentFrontend } from '@/config';
import messages from '@/features/translations/en-GB.json';
import { VehicleWarningItem, WarningTagItem } from '@/generated/GeneratedApiTypes';
import { WarningMessages, WarningTag, WarningTags } from './types';

const PREFIX = 'intl-msg:vehicle-warnings';

const hasTranslation = (key: string): boolean => key in messages;

const findTag = (tags: WarningTagItem[] | null | undefined, tagType: string): WarningTagItem | undefined =>
    tags?.find((t) => t.tag_type === tagType);

const batteryModelTag = (tags?: WarningTagItem[] | null): WarningTag | undefined => {
    const tag = findTag(tags, 'BATTERY_MODEL');
    const model = tag?.type;
    return model ? { id: `${PREFIX}.warningCode.replace_battery_pair.info`, values: { model } } : undefined;
};

const tyreInfoTag = (item: VehicleWarningItem): WarningTag | undefined => {
    const tag = findTag(item.tags, 'TYRE_INFO');
    const warningCode = tag?.warning_code ?? item.warning_code;
    if (!warningCode) return undefined;
    const infoTextKey = `${PREFIX}.warningCode.${warningCode.toLowerCase()}.info`;
    return hasTranslation(infoTextKey) ? { id: infoTextKey } : undefined;
};

const getAxleNumber = (position: string): string => {
    const lower = position.toLowerCase();
    if (lower === 'front_axle') return '1';
    const match = lower.match(/rear_axle_(\d+)/);
    return match ? String(Number(match[1]) + 1) : position;
};

const brakeLiningInfoTag = (tags?: WarningTagItem[] | null): WarningTag | undefined => {
    const axleTag = findTag(tags, 'AXLE_POSITION');
    if (!axleTag?.axle_position || !axleTag?.axle_side) return undefined;

    const axleNumber = getAxleNumber(axleTag.axle_position);
    const sideKey = `${PREFIX}.axleSide.${axleTag.axle_side.toLowerCase()}`;
    const axleSide = hasTranslation(sideKey) ? (messages as Record<string, string>)[sideKey] : axleTag.axle_side;

    return {
        id: `${PREFIX}.axle_number`,
        values: { axleNumber: `${axleNumber}, ${axleSide}` },
    };
};

const brakeLiningStatusTag = (tags?: WarningTagItem[] | null): WarningTag | undefined => {
    const statusTag = findTag(tags, 'STATUS_VALUE');
    if (!statusTag?.value) return undefined;
    return {
        id: `${PREFIX}.warningCode.brake_lining.status`,
        values: { value: statusTag.value },
    };
};

const buildWarningTags = (item: VehicleWarningItem): WarningTags => {
    switch (item.type) {
        case 'STARTER_BATTERY':
            return { info: batteryModelTag(item.tags) };

        case 'TYRES':
            return { info: tyreInfoTag(item) };

        case 'BRAKE_LININGS':
            return {
                info: brakeLiningInfoTag(item.tags),
                status: brakeLiningStatusTag(item.tags),
            };

        default:
            return {};
    }
};

export const buildWarningView = (item: VehicleWarningItem): WarningMessages | undefined => {
    const code = item.warning_code.toLowerCase();
    const description = `${PREFIX}.warningCode.${code}.description`;
    const recommendation = getRecommendationKey(item.type, code);

    if (!hasTranslation(description)) {
        return undefined;
    }

    return {
        description,
        recommendation,
        tags: buildWarningTags(item),
    };
};

const getRecommendationKey = (type: string, code: string): string => {
    if (type === 'BRAKE_LININGS') {
        const suffix = isWorkshopParentFrontend() ? 'workshop' : 'customer';
        const specificKey = `${PREFIX}.warningCode.${code}.recommendation.${suffix}`;
        if (hasTranslation(specificKey)) {
            return specificKey;
        }
    }
    return `${PREFIX}.warningCode.${code}.recommendation`;
};
